import {
  Input,
  Output,
  Component,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ElementRef,
  EventEmitter,
  AfterViewInit,
  ViewEncapsulation,
} from '@angular/core';
import { merge, Observable, OperatorFunction, of, Subject } from 'rxjs';
import { ngHelper } from '@cakeiteasy/web-utils';
import type { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap/typeahead';
import { debounceTime, finalize, map, switchMap, tap } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'ui-search-bar',
  templateUrl: './ui-search-bar.component.html',
  styleUrls: ['./ui-search-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class UiSearchBarComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild('defaultItemTemplate', { static: false })
  public defaultItemTemplate: TemplateRef<any>;
  @ViewChild('inputEl', { static: false }) public inputEl: ElementRef;

  @Input() fieldName = 'name';
  @Input() styleType = '';

  @Input() items: any[] = [];
  @Input() itemsSourceFn: (query) => Observable<any>;

  @Input() debounce = 200;
  @Input() minLength = 0;
  @Input() placeholder = '';
  @Input() itemTemplate: TemplateRef<any>;
  @Input() nothingFoundTemplate: TemplateRef<any>;
  @Input() resultFormatter: (item: any) => string;

  @Input() takeLimit: number;
  @Input() selectedItem: any;
  @Input() disabled = false;
  @Input() showLoading = false;
  @Input() showSearchIcon = true;
  @Input() cleanOnSelect = true;

  @Input() autoFocus = false;

  @Output() onSelected = new EventEmitter<any>();
  @Output() onInputFocused = new EventEmitter<any>();
  @Output() onNothingFound = new EventEmitter<boolean>();
  @Output() showDropdown = new EventEmitter<boolean>();

  public filteredItems: any[] = [];
  public asyncSelected: string;
  public source: OperatorFunction<string, readonly any[]>;

  public typeaheadLoading = false;
  public typeaheadNoResults: boolean;
  public lastQuery = null;

  private _inputClickListener: () => void;
  private _inputSearch$ = new Subject<string>();

  constructor(private renderer: Renderer2) {
    this.takeLimit = this.takeLimit || this.items?.length || 10;

    this.resultFormatter = this.resultFormatter
      ? this.resultFormatter
      : (item: any) => this.getValueByFieldName(item);

    this.source = (text$: Observable<string>) =>
      merge(text$.pipe(debounceTime(+this.debounce)), this._inputSearch$).pipe(
        switchMap((query: string) => this.getTypeaheadItems(query)),
      );
  }

  ngAfterViewInit() {
    this.itemTemplate = this.itemTemplate || this.defaultItemTemplate;

    if (this.autoFocus) {
      this.showDropdown.emit(false);
      this.inputEl.nativeElement.focus();
    }

    this._inputClickListener = this.renderer.listen(
      this.inputEl.nativeElement,
      'click',
      () => {
        this.showDropdown.emit(true);
        this.inputEl.nativeElement.focus();
        this.searchWithCurrentInputValue();
      },
    );

    this.renderer.listen(this.inputEl.nativeElement, 'input', () => {
      this.showDropdown.emit(true);
    });
  }

  onInputUnfocused() {
    if (this.selectedItem && !this.asyncSelected) {
      this.asyncSelected = this.resultFormatter(this.selectedItem);
    }

    if (this.typeaheadNoResults) {
      this.typeaheadNoResults = false;
    }
  }

  public handleInputFocused(): void {
    this.onInputFocused.emit();
    this.searchWithCurrentInputValue();
  }

  ngOnChanges(changes: SimpleChanges) {
    ngHelper.ifChanged(changes, 'selectedItem', (selectedItem) => {
      this.asyncSelected = this.resultFormatter(selectedItem);
    });
  }

  public selectFirstItem() {
    if (this.filteredItems && this.filteredItems.length) {
      const item = this.filteredItems[0];
      this.selectItem(item);
    } else {
      this.inputEl.nativeElement.focus();
    }
  }

  public getStatesAsObservable(token: string): Observable<any> {
    const query = new RegExp(token, 'ig');
    return of(
      this.items.filter((state: any) => {
        return query.test(this.getValueByFieldName(state));
      }),
    );
  }

  public getTypeaheadItems(query: string): Observable<any[]> {
    this.lastQuery = query;
    this.changeTypeaheadNoResults(false);

    if (query.length < this.minLength) {
      this.filteredItems = [];
      return of([]);
    }

    this.changeTypeaheadLoading(true);

    const items$ =
      typeof this.itemsSourceFn === 'function'
        ? this.itemsSourceFn(query)
        : this.getStatesAsObservable(query);

    return items$.pipe(
      map((items) => items.slice(0, this.takeLimit)),
      tap((items) => {
        this.filteredItems = query.length ? items : [];
        this.changeTypeaheadNoResults(query.length > 0 && !items.length);
      }),
      finalize(() => this.changeTypeaheadLoading(false)),
    );
  }

  public typeaheadOnSelect(e: NgbTypeaheadSelectItemEvent): void {
    this.selectItem(e.item);
  }

  public selectItem(item: any): void {
    this.onSelected.emit(item);
    this.inputEl.nativeElement.blur();

    if (this.cleanOnSelect) {
      this.asyncSelected = null;
    }
  }

  public changeTypeaheadNoResults(noResults: boolean): void {
    this.typeaheadNoResults = noResults;
    this.onNothingFound.emit(noResults);
  }

  public changeTypeaheadLoading(isLoading = false): void {
    setTimeout(() => (this.typeaheadLoading = isLoading));
  }

  public getValueByFieldName(item: any): string {
    return item ? item[this.fieldName] || '' : '';
  }

  private searchWithCurrentInputValue(): void {
    this._inputSearch$.next(this.asyncSelected || '');
  }

  public ngOnDestroy() {
    this._inputClickListener();
  }
}
